import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import HeroSection from "@/components/sections/HeroSection";
import UpdatesFeed from "@/components/sections/UpdatesFeed";
import InvestmentPortal from "@/components/sections/InvestmentPortal";
import TeslaSpaceXShowcase from "@/components/sections/TeslaSpaceXShowcase";
import TestimonialCarousel from "@/components/sections/TestimonialCarousel";
import EngagementTools from "@/components/sections/EngagementTools";
import FAQ from "@/components/sections/FAQ";
import ContactForm from "@/components/sections/ContactForm";
import Footer from "@/components/sections/Footer";
import ThemeToggle from "@/components/theme-toggle";

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation Bar - Tesla Style */}
      <nav className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-lg tracking-tight">
              MuskConnect Pro
            </div>
            {/* Desktop Menu */}
            <div className="hidden gap-8 md:flex">
              <a href="#updates" className="text-sm font-medium text-foreground hover:opacity-70 transition-opacity">
                Updates
              </a>
              <a href="#investment" className="text-sm font-medium text-foreground hover:opacity-70 transition-opacity">
                Investment
              </a>
              <a href="#showcase" className="text-sm font-medium text-foreground hover:opacity-70 transition-opacity">
                Showcase
              </a>
              <a href="#faq" className="text-sm font-medium text-foreground hover:opacity-70 transition-opacity">
                Help
              </a>
            </div>

            {/* Desktop CTA & Theme Toggle */}
            <div className="hidden gap-3 md:flex items-center">
              <ThemeToggle />
              <Button variant="outline" className="border-foreground text-foreground hover:bg-foreground hover:text-background">
                Sign In
              </Button>
              <Button className="bg-foreground text-background hover:bg-foreground/90">
                <a href="/steercode" className="w-full h-full flex items-center justify-center">
                  Enter SteerCode
                </a>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-3">
              <ThemeToggle />
              <button
                className="p-2 hover:bg-card transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="mt-4 space-y-3 pb-4 md:hidden">
              <a href="#updates" className="block text-sm font-medium hover:opacity-70">Updates</a>
              <a href="#investment" className="block text-sm font-medium hover:opacity-70">Investment</a>
              <a href="#showcase" className="block text-sm font-medium hover:opacity-70">Showcase</a>
              <a href="#faq" className="block text-sm font-medium hover:opacity-70">Help</a>
              <div className="pt-2 space-y-2 border-t border-border">
                <Button variant="outline" className="w-full border-foreground text-foreground hover:bg-foreground hover:text-background">
                  Sign In
                </Button>
                <Button className="w-full bg-foreground text-background hover:bg-foreground/90">
                  <a href="/steercode">Enter SteerCode</a>
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <HeroSection />

      {/* About Platform */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-medium mb-6 tracking-tight">The Future Awaits</h2>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Join a thriving community of innovators. MuskConnect Pro brings exclusive updates, 
            deep dives into groundbreaking technologies, and innovative investment opportunities. 
            Be part of the revolution.
          </p>
        </div>
      </section>

      {/* Updates Feed */}
      <section id="updates" className="py-24 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-4xl md:text-5xl font-medium mb-16 tracking-tight">Latest Updates</h2>
          <UpdatesFeed />
        </div>
      </section>

      {/* Investment Portal */}
      <section id="investment" className="py-24 px-4 sm:px-6 lg:px-8 bg-card border-t border-border">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-4xl md:text-5xl font-medium mb-16 tracking-tight">Exclusive Investment Program</h2>
          <InvestmentPortal />
        </div>
      </section>

      {/* Tesla & SpaceX Showcase */}
      <section id="showcase" className="py-24 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-4xl md:text-5xl font-medium mb-16 tracking-tight">Innovation Showcase</h2>
          <TeslaSpaceXShowcase />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-card border-t border-border">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-4xl md:text-5xl font-medium mb-16 tracking-tight">Community Voices</h2>
          <TestimonialCarousel />
        </div>
      </section>

      {/* Engagement Tools */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-4xl md:text-5xl font-medium mb-16 tracking-tight">Get Engaged</h2>
          <EngagementTools />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-4 sm:px-6 lg:px-8 bg-card border-t border-border">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-medium mb-16 tracking-tight">Questions?</h2>
          <FAQ />
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-4xl md:text-5xl font-medium mb-16 tracking-tight">Get in Touch</h2>
          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}